import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UserTable from "../components/common/user-table";
import EditUserPanel from "../components/common/edit-user-panel";
import Button from "../components/common/button";
import Dropdown from "../components/common/dropdown";
import { ManageStaffUserService } from "../services/ManageStaffUsers";
import { RestaurantsService } from "../services/Restaurants";
import { Snackbar, Alert } from "@mui/material";

export default function StaffManagement() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState(["Active"]);
  const [showEditUserPanel, setShowEditUserPanel] = useState(false);
  const [isUserUpdated, setIsUserUpdated] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserRemoved, setIsUserRemoved] = useState(null);
  const [filters, setFilters] = useState({
    role: null,
    status: null,
    restaurant: null,
    lastLogin: null,
  });
  const location = useLocation();
  const [refreshKey, setRefreshKey] = useState(0);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isOpenRows, setIsOpenRows] = useState(false);
  const rowsDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rowsDropdownRef.current && !rowsDropdownRef.current.contains(event.target)) {
        setIsOpenRows(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const restaurantsData = await RestaurantsService.getAllRestaurants('name');
        setRestaurants(restaurantsData);
      } catch (err) {
        setError("Failed to fetch data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const userData = location?.state?.user;
    if (userData) {
      setSnackbarOpen(true);
      setSnackbarMessage("User added successfully!");
      setSnackbarSeverity("success");
      // setUsers((prev) => [...prev, userData]); // Removed as fetching is now in UserTable
    }
    fetchData();
  }, []);


  useEffect(() => {
    if (isUserUpdated) {
      setSnackbarMessage("User updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsUserUpdated(false);
      setRefreshKey((prev) => prev + 1);
    }
  }, [isUserUpdated]);

  useEffect(() => {
    if (isUserRemoved) {
      setSnackbarMessage(
        `${isUserRemoved.first_name} ${isUserRemoved.last_name} was removed from your staff.`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsUserRemoved(null);
      setRefreshKey((prev) => prev + 1);
    }
  }, [isUserRemoved]);

  const filterOptions = {
    role: [
      { value: "all", label: "All Roles" },
      { value: "director", label: "Director" },
      { value: "employee", label: "Employee" },
      { value: "manager", label: "Manager" },
    ],
    status: [
      { value: "all", label: "All Statuses" },
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
    restaurant: [
      { value: "all", label: "All Restaurants" },
      ...restaurants.map((restaurant) => ({
        value: restaurant.uuid,
        label: restaurant.name,
      })),
    ],
    lastLogin: [
      { value: "all", label: "All Last Logins" },
      { value: "today", label: "Today" },
      { value: "week", label: "This Week" },
      { value: "month", label: "This Month" },
    ],
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filter) => {
    if (activeFilters.includes(filter)) {
      setActiveFilters(activeFilters.filter((f) => f !== filter));
    } else {
      setActiveFilters([...activeFilters, filter]);
    }
  };

  const handleAddUser = () => {
    navigate("/add-user");
  };

  const handleEditUser = (user) => {
    const formattedUser = {
      uuid: user.uuid,
      name: `${user.first_name} ${user.last_name}`,
      first_name: `${user.first_name}`,
      last_name: `${user.last_name}`,
      email: user.email,
      role: user.role === "super_admin" ? "Admin" : user.role, // Adjust role if necessary
      assigned_restaurants: user.assigned_restaurants
        ? user.assigned_restaurants.map((r) => r.uuid)
        : [], // Pass array of uuids
      status: user.active ? "Active" : "Inactive",
      last_login: user.last_login,
      lesson_progress: user.lesson_progress || [],
      trainingProgress:
        user?.lesson_progress?.length > 0
          ? (user?.lesson_progress?.filter((l) => l.completed).length /
            user?.lesson_progress?.length) *
          100
          : 0,
      // Add any other fields you want to display
    };
    setSelectedUser(formattedUser);
    setShowEditUserPanel(true);
  };

  const handleDropdownSelect = (type, option) => {
    console.log(option)
    console.log(type)
    setFilters((prev) => ({
      ...prev,
      [type]: option.value === "all" ? null : option.value,
    }));
  };

  // Removed client-side filtering logic

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen ">
      <div className="flex-1 overflow-auto">
        <main className="p-3 sm:p-5 lg:p-7">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-left">
                  <h1 className="text-xl font-semibold text-textcolor">
                    User Management
                  </h1>
                  <p className="text-sm text-gray-500">
                    Manage your team members and their access
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="relative" ref={rowsDropdownRef}>
                    <button
                      onClick={() => setIsOpenRows(!isOpenRows)}
                      className="flex items-center gap-3 bg-gray-50/80 px-4 py-2 rounded-xl border border-gray-100 shadow-sm hover:bg-white transition-all group h-[42px]"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#881337]"></div>
                        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest group-hover:text-gray-600">
                          Show:
                        </span>
                      </div>
                      <span className="text-[11px] font-black text-[#881337]">
                        {rowsPerPage} rows
                      </span>
                      <svg
                        className={`w-3 h-3 text-[#881337] transition-transform duration-300 ${isOpenRows ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {isOpenRows && (
                      <div className="absolute right-0 top-full mt-2 w-[140px] bg-white border border-gray-100 rounded-[1.25rem] shadow-xl shadow-gray-200/40 py-2.5 z-50 overflow-hidden">
                        {[10, 20, 50, 100].map((val) => (
                          <button
                            key={val}
                            onClick={() => {
                              setRowsPerPage(val);
                              setIsOpenRows(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-[10px] font-black uppercase tracking-wider transition-all ${rowsPerPage === val
                              ? "bg-[#881337] text-white"
                              : "text-gray-400 hover:bg-gray-50 hover:text-[#881337]"
                              }`}
                          >
                            {val} rows
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleAddUser}
                    className="flex items-center h-[42px]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add User
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-4">
              {/* ... search input ... */}
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="relative w-full md:w-auto">
                  <input
                    type="text"
                    placeholder="Search by name, email, or restaurant..."
                    className="w-full md:w-80 pl-10  pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <Dropdown
                  label="All Roles"
                  options={filterOptions.role}
                  selectedOption={filters.role}
                  onSelect={(option) => handleDropdownSelect("role", option)}
                />
                <Dropdown
                  label="All Statuses"
                  options={filterOptions.status}
                  selectedOption={filters.status}
                  onSelect={(option) => handleDropdownSelect("status", option)}
                />
                <Dropdown
                  label="All Restaurants"
                  options={filterOptions.restaurant}
                  selectedOption={filters.restaurant}
                  onSelect={(option) =>
                    handleDropdownSelect("restaurant", option)
                  }
                />
              </div>

              <UserTable
                filters={filters}
                searchQuery={searchQuery}
                onEditUser={handleEditUser}
                refreshKey={refreshKey}
                filterRestaurant={filters.restaurant}
                filterRole={filters.role}
                filterStatus={filters.status}
                itemsPerPage={rowsPerPage}
              />
            </div>
          </div>
        </main>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {showEditUserPanel && selectedUser && (
        <EditUserPanel
          user={selectedUser}
          setUsers={() => { }} // Placeholder or implement refetch trigger if needed
          setIsUserUpdated={setIsUserUpdated}
          setIsUserRemoved={setIsUserRemoved}
          onClose={() => setShowEditUserPanel(false)}
        />
      )}
    </div>
  );
}
